import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Avatar, Box, Button, Icon, Screen, Text, TextInput } from '~/atomic';
import modalStatusService from '~/services/modalStatus';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectUser, selectUserData } from '~/store/user/selectors';
import { updateUserPersonalData } from '~/store/user/thunks';
import { obfuscateCPF } from '~/utils/formatData';
import { maskCNPJ, maskPhone } from '~/utils/masks';

interface FormData {
  fullName: string;
  phone: string;
}

export default function UpdateProfileView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserData);
  const { loading } = useAppSelector(selectUser);

  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues: { fullName: user.fullName, phone: maskPhone(user.contact.phone) },
  });

  useEffect(() => {
    reset({ fullName: user.fullName, phone: maskPhone(user.contact.phone) });
  }, [user, reset]);

  const previewName = watch('fullName') || user.fullName;
  const documentLabel = user.userType === 'PF' ? 'CPF' : 'CNPJ';
  const documentValue = user.document
    ? user.userType === 'PF'
      ? obfuscateCPF(user.document)
      : maskCNPJ(user.document)
    : '—';

  const onSubmit = async (data: FormData) => {
    const ok = await dispatch(updateUserPersonalData(data)).unwrap();
    if (ok) {
      modalStatusService.show({
        type: 'success',
        title: 'Dados atualizados!',
        description: 'Suas informações foram salvas com sucesso.',
        textFirstButton: 'Ok',
        showFirstButton: true,
        onClickFirstButton: () => {
          modalStatusService.hide();
          navigate(-1);
        },
        handleCloseModal: () => {
          modalStatusService.hide();
          navigate(-1);
        },
      });
    }
  };

  return (
    <Screen headerTitle="Editar perfil" headerSubtitle="Atualize seus dados pessoais">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-medium py-medium">
        <div className="flex flex-col items-center text-center">
          <Avatar name={previewName} seed={user.fullName} size="xl" className="shadow-level1" />
          <Text className="mt-smaller text-paragraphLG color-complement-01" numberOfLines={1}>
            {previewName || 'Produtor'}
          </Text>
          <Text className="text-bodySM color-intense-06" numberOfLines={1}>
            {user.contact.email}
          </Text>
        </div>

        <Box className="rounded-large bg-intense-01 p-medium shadow-level1 flex flex-col gap-small">
          <Text className="text-captionLG uppercase tracking-wider color-intense-05">
            Dados editáveis
          </Text>
          <TextInput
            control={control}
            name="fullName"
            label="Nome completo"
            icon="icon-user"
            autoCapitalize="words"
          />
          <TextInput
            control={control}
            name="phone"
            label="Telefone"
            icon="icon-phone"
            keyboardType="phone-pad"
            mask="phone"
          />
        </Box>

        <Box className="rounded-large bg-intense-02 border border-intense-03 p-medium flex flex-col gap-smaller">
          <div className="flex items-center gap-quark">
            <Icon name="icon-lock" size={14} color="#808080" rounded={false} />
            <Text className="text-captionLG uppercase tracking-wider color-intense-05">
              Não editável por aqui
            </Text>
          </div>
          <div className="flex items-center justify-between gap-smaller">
            <Text className="text-bodySM color-intense-06">E-mail</Text>
            <Text className="text-bodyMD color-complement-01 truncate" numberOfLines={1}>
              {user.contact.email}
            </Text>
          </div>
          <div className="flex items-center justify-between gap-smaller">
            <Text className="text-bodySM color-intense-06">{documentLabel}</Text>
            <Text className="text-bodyMD color-complement-01">{documentValue}</Text>
          </div>
          <Text className="text-captionMD color-intense-06">
            Precisa alterar e-mail ou {documentLabel}? Fale com o suporte.
          </Text>
        </Box>

        <div className="flex gap-smaller">
          <Button
            variant="outline"
            text="Cancelar"
            onClick={() => navigate(-1)}
            className="flex-1"
          />
          <Button type="submit" text="Salvar" loading={loading} className="flex-1" />
        </div>
      </form>
    </Screen>
  );
}
